/**
 * AdminUsers.tsx — lista adminów platformy (F7a T7.9).
 *
 * Read-only w F7a. Akcje create/disable/reset 2FA → F10.
 * Dostępne dla adminów z uprawnieniem admin_users.manage (403 obsługiwany inline).
 *
 * Filtrowanie „jak w katalogu modułów" — w nagłówkach kolumn (client-side na pobranej
 * liście): rola = dropdown, aktywność = przełącznik, e-mail = szukajka. Nad tabelą
 * zostają tylko akcje (Zarządzaj rolami / Zaproś admina).
 *
 * Data: GET /api/admin/admins?limit=100.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { UserCog } from 'lucide-react';

import { AdminUserDrawer } from '@/components/AdminUserDrawer';
import { AdminUserInviteModal, type CreatedAdmin } from '@/components/AdminUserInviteModal';
import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

const ADMIN_ROLES = ['super_admin', 'support', 'finance', 'readonly'] as const;
type AdminRole = (typeof ADMIN_ROLES)[number];

/** Górny limit pobrania — pod client-side filtry per kolumna. */
const LIST_FETCH_LIMIT = 100;

const ROLE_BADGE_COLOR: Record<AdminRole, BadgeColor> = {
  super_admin: 'accent',
  support: 'blue',
  finance: 'amber',
  readonly: 'gray',
};

export interface AdminUserRow {
  id: string;
  email: string;
  role: string;
  totpEnabled: boolean;
  require2faSetup: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface AdminUsersResponse {
  admins: AdminUserRow[];
  total: number;
  limit: number;
  offset: number;
}

function formatDateTime(iso: string | null, fallback: string): string {
  if (!iso) return fallback;
  try {
    return new Date(iso).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function AdminUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { admin: currentAdmin, hasPermission } = useAuth();
  const toast = useToast();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [drawerAdminId, setDrawerAdminId] = useState<string | null>(null);

  const adminsQuery = useQuery<AdminUsersResponse>({
    queryKey: ['admin', 'admins'],
    queryFn: async () => {
      const res = await api.get<AdminUsersResponse>(`/admin/admins?limit=${LIST_FETCH_LIMIT}`);
      return res.data;
    },
    retry: false,
  });

  const columns = useMemo<ColumnDefinition<AdminUserRow>[]>(
    () => [
      {
        id: 'email',
        accessor: 'email',
        header: t('admin.adminUsers.columns.email'),
        sortable: true,
        renderCell: (_value, row) => <span className="text-sm text-text-primary">{row.email}</span>,
      },
      {
        id: 'role',
        accessor: (row) => row.role,
        header: t('admin.adminUsers.columns.role'),
        sortable: true,
        // Role rozłączne → dropdown wyboru (client-side, jak w katalogu modułów).
        headerFilter: {
          category: 'select',
          options: ADMIN_ROLES.map((r) => ({ value: r, label: t(`admin.adminUsers.role.${r}`) })),
        },
        renderCell: (_value, row) => {
          const color = ROLE_BADGE_COLOR[row.role as AdminRole] ?? 'gray';
          const label = t(`admin.adminUsers.role.${row.role}`, { defaultValue: row.role });
          return <Badge color={color}>{label}</Badge>;
        },
      },
      {
        id: 'twoFactor',
        // Filtr dwustanowy (Wszystkie/Tak/Nie) po włączonym 2FA — „Wymaga konfiguracji"
        // liczy się jako „Nie" (nie ma jeszcze aktywnego 2FA). Etykiety kontrolki są
        // generyczne (Tak/Nie), więc nie kolidują z etykietami-badge w kolumnie.
        accessor: (row) => row.totpEnabled,
        header: t('admin.adminUsers.columns.twoFactor'),
        headerFilter: { category: 'boolean' },
        renderCell: (_value, row) => {
          if (row.totpEnabled) {
            return <Badge color="green">{t('admin.adminUsers.twoFactor.enabled')}</Badge>;
          }
          if (row.require2faSetup) {
            return <Badge color="amber">{t('admin.adminUsers.twoFactor.setupRequired')}</Badge>;
          }
          return <Badge color="gray">{t('admin.adminUsers.twoFactor.disabled')}</Badge>;
        },
      },
      {
        id: 'isActive',
        accessor: (row) => row.isActive,
        header: t('admin.adminUsers.columns.isActive'),
        // Dwustanowe → przełącznik Wszystkie/Tak/Nie (client-side).
        headerFilter: { category: 'boolean' },
        renderCell: (_value, row) =>
          row.isActive ? (
            <Badge color="green">{t('admin.adminUsers.isActive.yes')}</Badge>
          ) : (
            <Badge color="red">{t('admin.adminUsers.isActive.no')}</Badge>
          ),
      },
      {
        id: 'lastLoginAt',
        accessor: (row) => row.lastLoginAt ?? '',
        header: t('admin.adminUsers.columns.lastLoginAt'),
        sortable: true,
        filterable: false,
        renderCell: (_value, row) => (
          <span className="font-mono text-xs text-text-secondary">
            {formatDateTime(row.lastLoginAt, t('admin.adminUsers.noLastLogin'))}
          </span>
        ),
      },
      {
        id: 'createdAt',
        accessor: (row) => row.createdAt,
        header: t('admin.adminUsers.columns.createdAt'),
        sortable: true,
        filterable: false,
        renderCell: (_value, row) => (
          <span className="font-mono text-xs text-text-secondary">
            {formatDateTime(row.createdAt, '—')}
          </span>
        ),
      },
    ],
    [t],
  );

  const renderActions = (row: AdminUserRow) => (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => setDrawerAdminId(row.id)}
      data-testid={`admin-users-details-${row.id}`}
    >
      {t('admin.adminUsers.actions.details')}
    </Button>
  );

  const data = adminsQuery.data;
  const rows = data?.admins ?? [];
  const emptyMessage = t('admin.adminUsers.emptyNoData');

  const isForbidden =
    adminsQuery.isError &&
    axios.isAxiosError(adminsQuery.error) &&
    adminsQuery.error.response?.status === 403;

  return (
    <Layout title={t('admin.adminUsers.title')}>
      <section className="px-6 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-text-primary">
            {t('admin.adminUsers.title')}
          </h2>
          <p className="mt-1 text-sm text-text-tertiary">{t('admin.adminUsers.subtitle')}</p>
        </div>

        {/* Filtr roli przeniesiony do nagłówka kolumny „Rola" (jak w katalogu modułów).
            Nad tabelą zostają tylko akcje. */}
        <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
          <Button variant="secondary" onClick={() => navigate('/admin-roles')}>
            {t('adminRoles.manageButton')}
          </Button>
          {hasPermission('admin_users.manage') && (
            <Button onClick={() => setInviteOpen(true)} data-testid="admin-users-invite-button">
              {t('admin.adminUsers.invite.openButton')}
            </Button>
          )}
        </div>

        {adminsQuery.isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={48} />
            ))}
          </div>
        ) : isForbidden ? (
          <div
            role="alert"
            className="rounded-xl border border-amber-300 bg-amber-50 p-6 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
          >
            <p>{t('admin.adminUsers.forbidden')}</p>
          </div>
        ) : adminsQuery.isError ? (
          <div
            role="alert"
            className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            <p>{t('admin.adminUsers.loadError')}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => void adminsQuery.refetch()}
            >
              {t('admin.adminUsers.retry')}
            </Button>
          </div>
        ) : (
          <>
            <PreviewTenantAuthBoundary>
              <ConfigurableTable<AdminUserRow>
                tableId="admin-users"
                columns={columns}
                data={rows}
                getRowId={(row) => row.id}
                renderActions={renderActions}
                actionsColumnLabel={t('admin.adminUsers.columns.actions')}
                persistConfig={false}
                showViewModeToggle={false}
                emptyIcon={UserCog}
                emptyTitle={emptyMessage}
                emptyDescription=""
              />
            </PreviewTenantAuthBoundary>
            {data && (
              <p className="mt-3 text-xs text-text-muted">
                {t('admin.adminUsers.countLabel', { count: data.total })}
                {data.total > rows.length &&
                  ' ' +
                    t('admin.adminUsers.countLabelPage', {
                      shown: rows.length,
                      total: data.total,
                    })}
              </p>
            )}
          </>
        )}
      </section>

      <AdminUserInviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={(created: CreatedAdmin) => {
          setInviteOpen(false);
          toast.show({
            variant: 'success',
            title: t('admin.adminUsers.invite.successTitle'),
            description: t('admin.adminUsers.invite.successDescription', { email: created.email }),
          });
          void adminsQuery.refetch();
        }}
      />

      <AdminUserDrawer
        open={drawerAdminId !== null}
        adminId={drawerAdminId}
        currentAdminId={currentAdmin?.id ?? null}
        onClose={() => setDrawerAdminId(null)}
        onChanged={() => void adminsQuery.refetch()}
      />
    </Layout>
  );
}
